// Mirror of the published LCR order contract: `LcrOrderResponse` from
// ITShopHWApiLambda (service/lcr-orders/lcr-orders.types.ts), whose status,
// delivery-method and submission shapes are published by
// `@amzn/it-shop-hw-event-schemas`. The website does not depend on that backend
// package, so this is a hand-kept mirror: changes to the published contract must
// be reflected here.

export const LCR_STATUS = {
  accepted: "ACCEPTED",
  processing: "PROCESSING",
  shipped: "SHIPPED",
  pendingPickup: "PENDING_PICKUP",
  delivered: "DELIVERED",
  processingIssue: "PROCESSING_ISSUE",
  cancelled: "CANCELLED",
  error: "ERROR",
  paused: "PAUSED",
} as const;

export type LcrStatus = (typeof LCR_STATUS)[keyof typeof LCR_STATUS];

export const LCR_DELIVERY_METHOD = {
  businessServiceCenter: "BSC",
  itOffice: "IT_OFFICE",
  shipToHome: "STH",
} as const;

export type LcrDeliveryMethod = (typeof LCR_DELIVERY_METHOD)[keyof typeof LCR_DELIVERY_METHOD];

export interface LcrRequester {
  alias: string;
  employeeId: string;
  fullName: string;
  email: string;
  companyName: string;
  employeeType: string;
  department: string;
  jobCode: string;
  jobFamily: string;
  jobLevelName: string;
  jobTitle: string;
  building: string;
  country: string;
}

export interface LcrShippingAddress {
  fullName: string;
  line1: string;
  line2?: string;
  line3?: string;
  city?: string;
  county?: string;
  state?: string;
  zip?: string;
  country: string;
  phoneNumber: string;
  deliveryBuilding?: string;
  deliveryInstructions?: string;
}

export interface LcrSubmission {
  requester: LcrRequester;
  newHardware: { laptop: string };
  replacedHardware: { laptop: { serialNumber: string } };
  shippingAddress: LcrShippingAddress;
  deliveryMethod: LcrDeliveryMethod;
  preferredKeyboardLayout?: string;
}

export interface LcrReplacedDevice {
  make: string;
  model: string;
}

export interface LcrSelectedDevice extends LcrReplacedDevice {
  specs: Record<string, string>;
}

export interface LcrOrder {
  orderId: string;
  login: string;
  serialNumber: string;
  attempt: number;
  clientRequestId: string;
  acceptedAt: string;
  updatedAt: string;
  submission: LcrSubmission;
  selectedDevice: LcrSelectedDevice;
  replacedDevice: LcrReplacedDevice;
  lcrStatus: LcrStatus;
  isDelayed: boolean;
  failureReason?: string;
  externalSystem?: string;
  externalOrderId?: string;
  externalStatus?: string;
  externalEventTime?: string;
}
